import { useMemo, useState } from "react";
import WeatherOnThisDay from "./WeatherOnThisDay";
import { CalendarDays } from "lucide-react";

// Village / heritage-site "Weather on the founding date" wrapper.
//
// Behaviour:
// • If the location doc supplies a founding date (props.foundingDate),
//   the WeatherOnThisDay panel renders it directly under a
//   "Weather on the founding date" heading.
// • Otherwise the widget renders a compact date picker so visitors
//   can explore historical weather at this location for ANY date
//   (birthday, wedding day, ancestor's death date…).
//
// Coordinates are optional — WeatherOnThisDay falls back to geocoding
// the provided `location` string via Nominatim.
export default function VillageWeatherWidget({
  foundingDate,   // ISO YYYY-MM-DD, optional
  lat,
  lon,
  location,       // e.g. "Vellaringattu, Kottayam, Kerala"
  name,           // village display name, used in heading
}) {
  const [pickedDate, setPickedDate] = useState(defaultPickerDate);
  const usingFounding = !!foundingDate && /^\d{4}-\d{2}-\d{2}/.test(String(foundingDate).slice(0, 10));
  const date = usingFounding ? String(foundingDate).slice(0, 10) : pickedDate;
  const label = useMemo(() => {
    if (usingFounding) return `Weather on the founding of ${name || "this place"}`;
    return "Historical weather at this location";
  }, [usingFounding, name]);

  return (
    <div data-testid="village-weather-widget">
      {!usingFounding && (
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <label className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>
            Pick any date to see the weather here
          </label>
          <input
            type="date"
            value={pickedDate}
            onChange={(e) => setPickedDate(e.target.value)}
            data-testid="village-weather-datepicker"
            max={new Date().toISOString().slice(0, 10)}
            className="rounded-full border px-3 py-1 text-xs bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
      )}
      <WeatherOnThisDay
        date={date}
        lat={lat}
        lon={lon}
        location={location}
        label={label}
      />
    </div>
  );
}

function defaultPickerDate() {
  // Default the picker to "today, 50 years ago" — feels like a real
  // historical time-travel moment on first load rather than showing
  // today's weather (which the CityPulse widget already covers).
  const d = new Date();
  d.setFullYear(d.getFullYear() - 50);
  return d.toISOString().slice(0, 10);
}
